/**
 * Classificação de severidade por tipo de alerta.
 * Decisão registrada na proposta inicial; alterações afetam o fluxo de
 * envio automático vs manual.
 */

export const TIPOS_ALERTA = [
  // CERT.br padrão "[AS NNN] serviço X habilitado"
  "tftp_aberto",
  "dns_resolver_aberto",
  "ntp_amplificador",
  "smtp_open_relay",
  "ssdp_aberto",
  "snmp_aberto",
  "mdns_aberto",
  "coap_aberto",
  "chargen_aberto",
  "netbios_aberto",
  "portmap_aberto",
  "ldap_aberto",
  // CERT.br variantes sem [AS NNN] no subject
  "servidor_ssh_vulneravel",
  // Outros provedores
  "ddos_origem", // ddos-response@nfoservers — IP da nossa rede atacando outro
  "abuse_report", // [noreply] abuse report about <IP> — provedor externo
  "ataque_externo_reportado", // Network attack received — provedor bilíngue
  // Genéricos
  "porta_admin_exposta",
  "servico_exposto",
  "botnet_malware",
  "vazamento_credenciais",
  "phishing",
  "spam",
  "outro",
] as const;

export type TipoAlerta = (typeof TIPOS_ALERTA)[number];

export const SEVERIDADES = ["baixa", "media", "alta", "critica"] as const;
export type Severidade = (typeof SEVERIDADES)[number];

export const SEVERIDADE_POR_TIPO: Record<TipoAlerta, Severidade> = {
  tftp_aberto: "alta",
  dns_resolver_aberto: "alta",
  ntp_amplificador: "alta",
  smtp_open_relay: "critica",
  ssdp_aberto: "alta",
  snmp_aberto: "alta",
  mdns_aberto: "media",
  coap_aberto: "media",
  chargen_aberto: "alta",
  netbios_aberto: "alta",
  portmap_aberto: "alta",
  ldap_aberto: "alta",
  servidor_ssh_vulneravel: "alta",
  ddos_origem: "critica", // IP da USE atacando — máxima urgência
  abuse_report: "alta",
  ataque_externo_reportado: "alta",
  porta_admin_exposta: "alta",
  servico_exposto: "media",
  botnet_malware: "critica",
  vazamento_credenciais: "critica",
  phishing: "critica",
  spam: "media",
  outro: "media",
};

export const TIPO_ALERTA_LABEL: Record<TipoAlerta, string> = {
  tftp_aberto: "TFTP aberto",
  dns_resolver_aberto: "DNS resolver aberto",
  ntp_amplificador: "NTP amplificador",
  smtp_open_relay: "SMTP open relay",
  ssdp_aberto: "SSDP aberto",
  snmp_aberto: "SNMP aberto",
  mdns_aberto: "mDNS aberto",
  coap_aberto: "CoAP aberto",
  chargen_aberto: "CharGen aberto",
  netbios_aberto: "NetBIOS aberto",
  portmap_aberto: "Portmap aberto",
  ldap_aberto: "LDAP aberto",
  servidor_ssh_vulneravel: "Servidor SSH vulnerável",
  ddos_origem: "DDoS originado da nossa rede",
  abuse_report: "Abuse report externo",
  ataque_externo_reportado: "Ataque reportado por terceiro",
  porta_admin_exposta: "Porta administrativa exposta",
  servico_exposto: "Serviço exposto sem autenticação",
  botnet_malware: "Botnet / Malware",
  vazamento_credenciais: "Vazamento de credenciais",
  phishing: "Phishing",
  spam: "Spam",
  outro: "Outro",
};

/**
 * Severidades que disparam fluxo de aprovação manual.
 *
 * Decisão do usuário (08/06/2026): TODAS as severidades vão para envio
 * automático, independente do nível. Os guard-rails que continuam ativos
 * (kill switch, política do cliente != auto, quarentena de cliente novo,
 * rate limit por hora) seguem dando segurança contra disparo descontrolado.
 *
 * Se quiser reativar o gate por severidade no futuro, adicione "alta" e/ou
 * "critica" de volta a este set.
 */
export const SEVERIDADES_MANUAIS: ReadonlySet<Severidade> = new Set([]);

export function ehManual(s: Severidade): boolean {
  return SEVERIDADES_MANUAIS.has(s);
}
